import { ReactNode, useEffect, useState } from 'react';
import { Search, Filter, RefreshCw } from 'lucide-react';

interface Breadcrumb {
  label: string;
  href?: string;
}

interface PageHeaderProps {
  title: string;
  description?: string;
  breadcrumbs?: Breadcrumb[];
  actions?: ReactNode;
  // Search
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  // Date filter
  dateFilterKey?: string; // sessionStorage key
  dateFrom?: string;
  dateTo?: string;
  onDateChange?: (from: string, to: string) => void;
  // Advanced search
  onAdvancedSearch?: () => void;
  hasAdvancedFilters?: boolean;
  // Apply
  onApply?: () => void;
}

// デフォルト日付を取得（30日前〜今日）
function getDefaultDates() {
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);
  return {
    from: thirtyDaysAgo.toISOString().split('T')[0],
    to: today.toISOString().split('T')[0],
  };
}

export default function PageHeader({ 
  title, 
  description, 
  breadcrumbs, 
  actions,
  searchValue,
  onSearchChange,
  searchPlaceholder = '検索...',
  dateFilterKey,
  dateFrom,
  dateTo,
  onDateChange,
  onAdvancedSearch,
  hasAdvancedFilters,
  onApply,
}: PageHeaderProps) {
  // セッションから日付を復元
  useEffect(() => {
    if (dateFilterKey && onDateChange) {
      const saved = sessionStorage.getItem(dateFilterKey);
      if (saved) {
        const { from, to } = JSON.parse(saved);
        onDateChange(from, to);
      } else {
        const defaults = getDefaultDates();
        onDateChange(defaults.from, defaults.to);
      }
    }
  }, [dateFilterKey]);

  // 日付変更時にセッションに保存
  const handleDateChange = (type: 'from' | 'to', value: string) => {
    if (!onDateChange) return;
    const newFrom = type === 'from' ? value : (dateFrom || '');
    const newTo = type === 'to' ? value : (dateTo || '');
    onDateChange(newFrom, newTo);
    if (dateFilterKey) {
      sessionStorage.setItem(dateFilterKey, JSON.stringify({ from: newFrom, to: newTo }));
    }
  };

  const breadcrumbLabel = breadcrumbs && breadcrumbs.length > 0 
    ? breadcrumbs.map(b => b.label).join(' > ')
    : '';

  return (
    <div className="mb-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* Left: Title with breadcrumb */}
        <div className="flex items-center gap-2">
          <h1 className="text-base font-bold whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>
            {title}
          </h1>
          {breadcrumbLabel && (
            <span className="text-xs whitespace-nowrap" style={{ color: 'var(--text-tertiary)' }}>
              ({breadcrumbLabel})
            </span>
          )}
        </div>

        {/* Right: Date + Search + Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Date Filter */}
          {onDateChange && (
            <div className="flex items-center gap-1">
              <input
                type="date"
                value={dateFrom || ''}
                onChange={(e) => handleDateChange('from', e.target.value)}
                className="input-field py-1 text-sm w-32"
              />
              <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>〜</span>
              <input
                type="date"
                value={dateTo || ''}
                onChange={(e) => handleDateChange('to', e.target.value)}
                className="input-field py-1 text-sm w-32"
              />
            </div>
          )}

          {/* Search */}
          {onSearchChange && (
            <div className="relative flex items-center">
              <Search
                size={14}
                className="absolute pointer-events-none z-10"
                style={{ color: 'var(--text-tertiary)', left: '8px' }}
              />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchValue || ''}
                onChange={(e) => onSearchChange(e.target.value)}
                className="input-field text-sm w-48"
                style={{ padding: '10px 14px 10px 25px' }}
              />
            </div>
          )}

          {/* Apply Button */}
          {onApply && (
            <button
              onClick={onApply}
              className="btn btn-primary btn-sm"
              title="反映"
            >
              <RefreshCw size={14} />
              反映
            </button>
          )}

          {/* Advanced Search */}
          {onAdvancedSearch && (
            <button
              onClick={onAdvancedSearch}
              className={`btn btn-sm ${hasAdvancedFilters ? 'btn-primary' : 'btn-outline'}`}
              title="詳細検索"
            >
              <Filter size={14} />
              詳細
            </button>
          )}

          {/* Actions */}
          {actions}
        </div>
      </div>
    </div>
  );
}


